import { useEffect, useState, useCallback } from "react";
import BonusGrid from "../components/BonusGrid";
import Header from "../components/Header";
import Navigation from "../components/Navigation";
import LanguageSelector from "../components/LanguageSelector";
import MaintenanceMode from "../components/MaintenanceMode";
import { useStatic } from "../context/StaticContext";
import { useBonus } from "../context/BonusContext";
import { useAdmin } from "../context/AdminContextNew";
import { useMaintenanceMode } from "../context/MaintenanceContext";
import AdminLoginModal from "../components/AdminLoginModal";
import CssEditButton from "../components/CssEditButton";
import CssPanelNew from "../components/CssPanelNew";
import { CircularProgress } from "@mui/material";
import { useCss } from "../context/CssContext";
import SeasonalEffect from "../components/SeasonalModes";
import config from "../config";

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('http')) return url;
  const prefix = config.imgUrl && config.imgUrl.endsWith('/') ? config.imgUrl : `${config.imgUrl || ''}/`;
  const cleanedImg = url.startsWith('/') ? url.slice(1) : url;
  return `${prefix}${cleanedImg}`;
};

const HomePage = () => {
  const { updateMode, isLanguage } = useStatic();
  const { fetchBonusForUser, fetchGamesForUser, userName, loading, error, userId } = useBonus();
  const { isMaintenanceMode } = useMaintenanceMode();
  const {
    showLoginModal,
    setShowLoginModal,
    handleLogin,
    showCssEditButton,
    handleEditCss,
    showCssPanel,
    handleCloseCssPanel,
  } = useAdmin();

  const { customBgImage, customBgEnabled, seasonalMode, customLogoImage, customLogoEnabled, longLogoVariant } = useCss();

  const [spinnerSize, setSpinnerSize] = useState(60);
  const [loadingLogo, setLoadingLogo] = useState(null);
  const [footerLogo, setFooterLogo] = useState(null);
  const [loadingBgColor, setLoadingBgColor] = useState("linear-gradient(135deg, #0a1a28 0%, #0f212e 50%, #152b3c 100%)");
  const [spinnerColor, setSpinnerColor] = useState("#1475E1");
  const [loadingTextColor, setLoadingTextColor] = useState("#666");
  const [backgroundStyle, setBackgroundStyle] = useState(null);

  // Özel arkaplan aktifse görseli uygula, değilse sıfırla
  // Not: app-container'a değil, sabit position'lı bir katmana yansıtıyoruz
  // → her zaman tam viewport'u kaplar, scroll'da kaymaz, container yüksekliğine bağlı kalmaz
  useEffect(() => {
    if (customBgEnabled && customBgImage) {
      const imgUrl = getImageUrl(customBgImage);
      setBackgroundStyle({
        backgroundImage: `url('${imgUrl}')`,
        backgroundSize: "cover",
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat",
      });
    } else {
      setBackgroundStyle(null);
    }
  }, [customBgEnabled, customBgImage]);

  // Loading spinner size ve renkleri CSS değişkeninden oku
  useEffect(() => {
    const getCSSValue = (property, fallbackKey) => {
      const value = getComputedStyle(document.documentElement)
        .getPropertyValue(property)
        .trim();
      return value || (fallbackKey ? localStorage.getItem(fallbackKey) : null) || null;
    };

    const updateLoadingSettings = () => {
      const size = getCSSValue('--loading-page-spinner-size');
      if (size) setSpinnerSize(parseInt(size) || 60);

      const bgColor = getCSSValue('--loading-page-bg-color', 'loadingPageBgColor');
      if (bgColor) setLoadingBgColor(bgColor);

      const spinnerColor = getCSSValue('--loading-page-spinner-color', 'loadingPageSpinnerColor');
      if (spinnerColor) setSpinnerColor(spinnerColor);

      const textColor = getCSSValue('--loading-page-text-color', 'loadingPageTextColor');
      if (textColor) setLoadingTextColor(textColor);
    };
    
    updateLoadingSettings();
    
    const observer = new MutationObserver(updateLoadingSettings);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style'],
    });
    
    return () => observer.disconnect();
  }, []);

  // Logo yükleme helper
  const loadLogo = useCallback((src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(src);
      img.onerror = reject;
      img.src = src;
    });
  }, []);

  // Logo yükleme (fallback destekli)
  const loadLogoWithFallback = useCallback(async (paths, setState) => {
    for (const path of paths) {
      try {
        const logoPath = await loadLogo(path);
        setState(logoPath);
        return;
      } catch {
        continue;
      }
    }
    setState(null);
  }, [loadLogo]);

  // Logo'ları yükle: loading (logo.webp) ve footer (long.svg / long-white.svg)
  useEffect(() => {
    loadLogoWithFallback(['/static/image/logo.webp'], setLoadingLogo);
    const footerCandidates = longLogoVariant === 'white'
      ? ['/static/image/long-white.svg', '/static/image/long.svg', '/static/image/long.webp']
      : ['/static/image/long.svg', '/static/image/long.webp'];
    loadLogoWithFallback(footerCandidates, setFooterLogo);
  }, [loadLogoWithFallback, longLogoVariant]);

  useEffect(() => {
    fetchBonusForUser();
    fetchGamesForUser();
  }, [fetchBonusForUser, fetchGamesForUser]);

  // Bakım modu değiştiğinde bonusları ve oyunları yeniden yükle
  useEffect(() => {
    if (isMaintenanceMode) {
      fetchBonusForUser();
      fetchGamesForUser();
    }
  }, [isMaintenanceMode, fetchBonusForUser, fetchGamesForUser]);

  // Dil değiştiğinde bonusları ve oyunları yeniden yükle
  useEffect(() => {
    if (isLanguage) {
      fetchBonusForUser();
      fetchGamesForUser();
    }
  }, [isLanguage, fetchBonusForUser, fetchGamesForUser]);

  if (updateMode) {
    return <MaintenanceMode />;
  }

  // Hata durumlarını kontrol et
  if (error) {
    const getErrorMessage = (errorType) => {
      switch (errorType) {
        case "invalid_access":
          return "Geçersiz erişim";
        case "user_blocked":
          return "Kullanıcı engellenmiştir";
        case "user_not_found":
          return "Kullanıcı bulunamadı";
        case "api_error":
          return "API hatası oluştu";
        case "connection_error":
          return "Bağlantı hatası";
        default:
          return "Bilinmeyen hata";
      }
    };

    return (
      <div className="loading-container">
        <div className="error-message">
          <h2 style={{ color: "var(--error-page-heading-color, #ff4757)", marginBottom: "10px" }}>
            {getErrorMessage(error)}
          </h2>
          <p style={{ color: "var(--error-page-text-color, #666666)", fontSize: "14px" }}>
            Lütfen URL'yi kontrol edin veya daha sonra tekrar deneyin.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    const displayLoadingLogo = customLogoEnabled && customLogoImage ? getImageUrl(customLogoImage) : loadingLogo;
    return (
      <div
        className="loading-container"
        style={{
          background: loadingBgColor,
        }}
      >
        {displayLoadingLogo && (
          <img
            src={displayLoadingLogo}
            alt="Logo"
            className="loading-logo"
          />
        )}
        <CircularProgress
          size={spinnerSize}
          thickness={4}
          sx={{
            color: spinnerColor,
            "& .MuiCircularProgress-circle": {
              strokeLinecap: "round",
            },
          }}
        />
        {!userId && (
          <p style={{ marginTop: "20px", color: loadingTextColor }}>
            Geçersiz erişim
          </p>
        )}
      </div>
    );
  }
  return (
    <div className="app-container">
      {/* Özel arkaplan: tam viewport'u kaplayan sabit katman */}
      {backgroundStyle && <div className="app-bg-layer" style={backgroundStyle} aria-hidden="true" />}
      {/* Sezonluk Mod Efekti (kar, halloween, sevgililer, şans, fişek, ramazan, maç günü) */}
      <SeasonalEffect mode={seasonalMode} />

      {/* Admin Login Modal */}
      <AdminLoginModal
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />

      {/* CSS Edit Button */}
      {showCssEditButton && <CssEditButton onClick={handleEditCss} />}

      {/* CSS Panel */}
      <CssPanelNew open={showCssPanel} onClose={handleCloseCssPanel} />

              {/* Language Selector - sağ üst köşede sabit */}
        {isLanguage && <LanguageSelector />}

        {/* Ana İçerik */}
        <div className="content-container">
        {/* Header */}
        <Header username={userName} />
        {/* Navigation */}
        <Navigation />

        {/* Otobonüs Bonus Kartları */}
        <BonusGrid />
       
      </div>

      {/* Footer */}
      <footer className="app-footer">
        {footerLogo && (
          <div className="footer-logo-wrapper">
            <a 
              href="https://longdijital.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="footer-logo-link"
            >
              <div className="footer-logo-container">
                <img 
                  src={footerLogo}
                  alt="Long Dijital Logo"
                  className="footer-logo"
                />
                <div className="footer-logo-glow"></div>
              </div>
            </a>
          </div>
        )}
      </footer>
    </div>
  );
};

export default HomePage;

